import { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  List,
  message,
  Modal,
  Tag,
  Timeline,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getChequeById, postChequeContato } from "@/services/cheque";
import { ChequeDetalheType, StatusChequeCor } from "@/types/ChequeType";

type Props = {
  chequeId: number | null;
  onClose: () => void;
  onChanged: () => void;
};

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const formatData = (data?: string) => (data ? dayjs(data).format("DD/MM/YYYY") : "-");

const ContatosDrawer: React.FC<Props> = ({ chequeId, onClose, onChanged }) => {
  const [detalhe, setDetalhe] = useState<ChequeDetalheType | null>(null);
  const [novoContatoAberto, setNovoContatoAberto] = useState(false);
  const [dataContato, setDataContato] = useState<Dayjs | null>(dayjs());
  const [acordo, setAcordo] = useState("");
  const [prazo, setPrazo] = useState<Dayjs | null>(null);
  const [nomeContato, setNomeContato] = useState("");

  const carregarDetalhe = useCallback(async () => {
    if (chequeId === null) return;
    try {
      const { data } = await getChequeById(chequeId);
      setDetalhe(data);
    } catch {
      message.error("Erro ao carregar os dados do cheque.");
    }
  }, [chequeId]);

  useEffect(() => {
    setDetalhe(null);
    carregarDetalhe();
  }, [carregarDetalhe]);

  const salvarContato = async () => {
    if (chequeId === null) return;
    if (!dataContato) {
      message.warning("Informe a data do contato.");
      return;
    }
    if (!acordo.trim()) {
      message.warning("Informe o que foi acordado.");
      return;
    }
    if (!nomeContato.trim()) {
      message.warning("Informe o nome de quem fez o contato.");
      return;
    }

    try {
      await postChequeContato({
        alunoChequeId: chequeId,
        dataContato: dataContato.format("YYYY-MM-DD"),
        acordo,
        prazo: prazo ? prazo.format("YYYY-MM-DD") : undefined,
        nomeContato,
      });
      message.success("Contato registrado com sucesso!");
      setNovoContatoAberto(false);
      setDataContato(dayjs());
      setAcordo("");
      setPrazo(null);
      setNomeContato("");
      carregarDetalhe();
      onChanged();
    } catch (error: any) {
      message.error(error?.response?.data ?? "Erro ao registrar o contato.");
    }
  };

  return (
    <Drawer
      title={`Cheque nº ${detalhe?.numero ?? ""} — ${detalhe?.nomeDonoCheque ?? ""}`}
      open={chequeId !== null}
      onClose={onClose}
      width={560}
    >
      {detalhe && (
        <>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Status" span={2}>
              <Tag color={detalhe.status ? StatusChequeCor[detalhe.status] : undefined}>
                {detalhe.statusLabel}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Matrícula">{detalhe.matricula}</Descriptions.Item>
            <Descriptions.Item label="Aluno">{detalhe.nomeAluno}</Descriptions.Item>
            <Descriptions.Item label="Valor">{formatValor(detalhe.valor)}</Descriptions.Item>
            <Descriptions.Item label="Data">{formatData(detalhe.dataVencimento)}</Descriptions.Item>
            {detalhe.motivoDevolucao && (
              <Descriptions.Item label="Motivo Devolução" span={2}>
                {detalhe.motivoDevolucao}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Divider orientation="left">
            Contatos com o responsável
            <Button
              type="primary"
              size="small"
              shape="circle"
              icon={<PlusOutlined />}
              className="ml-2"
              onClick={() => setNovoContatoAberto(true)}
            />
          </Divider>

          <List
            dataSource={detalhe.contatos}
            locale={{ emptyText: "Nenhum contato registrado" }}
            renderItem={(contato) => (
              <List.Item>
                <List.Item.Meta
                  title={`${formatData(contato.dataContato)} — ${contato.nomeContato}`}
                  description={
                    <>
                      <div>{contato.acordo}</div>
                      {contato.prazo && (
                        <div>
                          <strong>Prazo:</strong> {formatData(contato.prazo)}
                        </div>
                      )}
                    </>
                  }
                />
              </List.Item>
            )}
          />

          <Divider orientation="left">Histórico do cheque</Divider>
          <Timeline
            items={detalhe.historicos.map((h) => ({
              children: (
                <>
                  <div>
                    {h.statusAnterior ? `${h.statusAnterior} → ` : ""}
                    <strong>{h.statusNovo}</strong>
                  </div>
                  {h.observacao && <div>{h.observacao}</div>}
                  <div className="text-xs opacity-70">
                    {dayjs(h.dataRegistro).format("DD/MM/YYYY HH:mm")} — {h.usuarioNome}
                  </div>
                </>
              ),
            }))}
          />
        </>
      )}

      <Modal
        title="Novo Contato"
        open={novoContatoAberto}
        onOk={salvarContato}
        onCancel={() => setNovoContatoAberto(false)}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form layout="vertical">
          <Form.Item label="Data do contato" required>
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              value={dataContato}
              onChange={(data) => setDataContato(data)}
            />
          </Form.Item>
          <Form.Item label="O que foi acordado" required>
            <Input.TextArea
              rows={3}
              maxLength={500}
              value={acordo}
              onChange={(e) => setAcordo(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Prazo para regularização / próximo contato">
            <DatePicker
              className="w-full"
              format="DD/MM/YYYY"
              value={prazo}
              onChange={(data) => setPrazo(data)}
            />
          </Form.Item>
          <Form.Item label="Quem fez o contato" required>
            <Input
              maxLength={100}
              value={nomeContato}
              onChange={(e) => setNomeContato(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
};

export default ContatosDrawer;
